import React, { useMemo } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { FormField, Select, Spinner } from '@legalshield/adonis-ux-framework';

import { useCountiesByStates } from '../queriesClient';
import { StateAndCounty } from '../types';
import SearchWithDropDown, { DropDownOption } from './SearchWithDropDown';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  availableStateCodes: string[];
  required?: boolean;
};

const LocationMapperStateCounty = <T extends FieldValues>({
  control,
  name,
  availableStateCodes,
  required,
}: Props<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => {
      const value = (field.value as StateAndCounty | undefined) ?? { state: '', counties: [] };
      const selectedState = value.state;
      return (
        <div className="rnq-field-group">
          <FormField
            id={`${String(name)}-state`}
            label="State"
            required={required}
            status={fieldState.error ? 'invalid' : undefined}
            validationHint={fieldState.error?.message}
          >
            <Select
              placeholder="Select a state"
              placeholderValue=""
              value={selectedState}
              options={availableStateCodes.map((code) => ({ label: code, value: code }))}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                field.onChange({ state: e.target.value, counties: [] })
              }
            />
          </FormField>

          {selectedState && (
            <CountyPicker
              state={selectedState}
              selected={value.counties.map((c) => ({ title: c.title, value: c.value }))}
              onChange={(next) =>
                field.onChange({
                  state: selectedState,
                  counties: next.map((n) => ({
                    title: n.title,
                    value: n.value,
                    countryCode: 'US',
                  })),
                })
              }
              required={required}
            />
          )}
        </div>
      );
    }}
  />
);

const CountyPicker = ({
  state,
  selected,
  onChange,
  required,
}: {
  state: string;
  selected: DropDownOption[];
  onChange: (next: DropDownOption[]) => void;
  required?: boolean;
}) => {
  const { data, isLoading, isError } = useCountiesByStates([state]);

  const options = useMemo<DropDownOption[]>(() => {
    if (!data?.length) return [];
    const first = data[0];
    const countyList = first?.response?.[state] ?? [];
    return countyList
      .map((county) => ({ title: county, value: county }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [data, state]);

  if (isLoading) {
    return (
      <FormField id="county-picker" label="Counties" required={required}>
        <Spinner />
      </FormField>
    );
  }
  if (isError) {
    return (
      <FormField id="county-picker" label="Counties" required={required}>
        <p className="rnq-error">Could not load counties for {state}.</p>
      </FormField>
    );
  }
  if (options.length === 0) {
    return (
      <FormField id="county-picker" label="Counties" required={required}>
        <p>No counties available for {state}.</p>
      </FormField>
    );
  }

  return (
    <SearchWithDropDown
      label="Counties"
      required={required}
      multipleChoice
      multiSize="small"
      options={options}
      value={selected}
      onChange={onChange}
    />
  );
};

export default LocationMapperStateCounty;
